/**
 * Holdback Panel — Phase 1c.2
 *
 * Advisory list of holdbacks for a contractor (or all contractors when no
 * `contractorId` prop is passed). Every state-changing action requires a
 * typed reason ≥10 chars. Append-only — release/pause/resume/reverse are
 * additional ledger entries, never edits.
 *
 * Soft-mode language only. Never auto-executes financial moves.
 */
import { useMemo, useState } from "react";
import { ShieldAlert, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppData } from "@/data/AppDataContext";
import {
  createHoldback,
  releaseHoldback,
  pauseHoldbackForDispute,
  resumeHoldback,
  reverseHoldbackEntry,
  deriveAllHoldbackStates,
  type HoldbackState,
} from "@/data/contractor/networkExecution";
import { useRole } from "@/data/RoleContext";
import { toast } from "sonner";

type ActionKind = "create" | "release" | "pause" | "resume" | "reverse";

interface Props {
  /** When set, only this contractor's holdbacks are shown and the create
   * dialog defaults to them. When omitted, panel is admin-mode. */
  contractorId?: string;
  /** Read-only mode (e.g., contractor portal Profile section). */
  readOnly?: boolean;
}

const ACTION_LABELS: Record<ActionKind, string> = {
  create: "Create holdback",
  release: "Release",
  pause: "Pause for dispute",
  resume: "Resume",
  reverse: "Reverse last entry",
};

export function HoldbackPanel({ contractorId, readOnly }: Props) {
  const {
    holdbackLedger,
    setHoldbackLedger,
    contractors,
    jobs,
    disputeEvents,
  } = useAppData();
  const { role } = useRole();

  const states = useMemo(() => {
    const all = deriveAllHoldbackStates(holdbackLedger);
    return contractorId ? all.filter((s) => s.contractorId === contractorId) : all;
  }, [holdbackLedger, contractorId]);

  // ── Action dialog ────────────────────────────────────────────
  const [action, setAction] = useState<{
    kind: ActionKind;
    state?: HoldbackState;
  } | null>(null);
  const [form, setForm] = useState({
    jobId: "",
    contractorId: contractorId ?? "",
    amount: "",
    reason: "",
    disputeId: "",
    reverseEntryId: "",
  });
  const [error, setError] = useState<string | null>(null);

  const open = (kind: ActionKind, state?: HoldbackState) => {
    setAction({ kind, state });
    setError(null);
    setForm({
      jobId: state?.jobId ?? "",
      contractorId: state?.contractorId ?? contractorId ?? "",
      amount: kind === "create" ? "" : String(state?.netHeld ?? ""),
      reason: "",
      disputeId: "",
      reverseEntryId: state?.entries[state.entries.length - 1]?.id ?? "",
    });
  };

  const submit = () => {
    if (!action) return;
    setError(null);
    const base = {
      ledger: holdbackLedger,
      jobId: form.jobId,
      contractorId: form.contractorId,
      amount: Number(form.amount) || 0,
      reason: form.reason,
      actorId: role,
      actorRole: role,
    } as const;
    let result;
    switch (action.kind) {
      case "create":
        result = createHoldback(base);
        break;
      case "release":
        if (!action.state) return;
        result = releaseHoldback({ ...base, holdbackId: action.state.holdbackId });
        break;
      case "pause":
        if (!action.state) return;
        if (!form.disputeId.trim()) {
          setError("Link a dispute id to pause this holdback.");
          return;
        }
        result = pauseHoldbackForDispute({
          ...base,
          holdbackId: action.state.holdbackId,
          disputeId: form.disputeId,
        });
        break;
      case "resume":
        if (!action.state) return;
        result = resumeHoldback({ ...base, holdbackId: action.state.holdbackId });
        break;
      case "reverse":
        if (!form.reverseEntryId) {
          setError("Select an entry to reverse.");
          return;
        }
        result = reverseHoldbackEntry({
          ...base,
          reversesEntryId: form.reverseEntryId,
        });
        break;
    }
    if (!result.entry) {
      setError(result.error ?? "Could not record action.");
      return;
    }
    setHoldbackLedger(result.ledger);
    toast.success(`${ACTION_LABELS[action.kind]} recorded.`);
    setAction(null);
  };

  const openDisputesById = useMemo(() => {
    const ids = new Set<string>();
    for (const e of disputeEvents) ids.add(e.disputeId);
    return Array.from(ids);
  }, [disputeEvents]);

  return (
    <Card className="p-3 space-y-3" data-testid="holdback-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4 text-warning" />
            Payout holdbacks
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Advisory ledger. Every entry is append-only and requires a reason.
          </p>
        </div>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={() => open("create")}>
            <Plus className="h-3.5 w-3.5 mr-1" /> New
          </Button>
        )}
      </div>

      {states.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">No holdbacks recorded.</div>
      ) : (
        <ul className="space-y-2" data-testid="holdback-list">
          {states.map((s) => {
            const c = contractors.find((c) => c.id === s.contractorId);
            const j = jobs.find((j) => j.id === s.jobId);
            return (
              <li
                key={s.holdbackId}
                className="rounded-md border border-border/60 p-2.5 text-xs space-y-1.5"
                data-testid={`holdback-${s.holdbackId}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {c?.companyName ?? s.contractorId} • {j?.id ?? s.jobId}
                    </div>
                    <div className="text-muted-foreground">
                      Net held: ${s.netHeld.toLocaleString()} • {s.entries.length} entries
                    </div>
                  </div>
                  <Badge
                    variant={
                      s.status === "active"
                        ? "destructive"
                        : s.status === "paused"
                        ? "outline"
                        : "secondary"
                    }
                    className="text-[10px] capitalize"
                  >
                    {s.status}
                  </Badge>
                </div>
                {!readOnly && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {s.status === "active" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => open("release", s)}>
                          Release
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => open("pause", s)}>
                          Pause for dispute
                        </Button>
                      </>
                    )}
                    {s.status === "paused" && (
                      <Button size="sm" variant="outline" onClick={() => open("resume", s)}>
                        Resume
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => open("reverse", s)}>
                      Reverse last entry
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{action ? ACTION_LABELS[action.kind] : ""}</DialogTitle>
            <DialogDescription>
              All holdback actions are append-only. State is derived from the ledger.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {action?.kind === "create" && (
              <>
                <div>
                  <label className="text-xs font-medium">Job ID</label>
                  <Input
                    value={form.jobId}
                    onChange={(e) => setForm({ ...form, jobId: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">Contractor ID</label>
                  <Input
                    value={form.contractorId}
                    onChange={(e) => setForm({ ...form, contractorId: e.target.value })}
                  />
                </div>
              </>
            )}
            {(action?.kind === "create" || action?.kind === "release") && (
              <div>
                <label className="text-xs font-medium">Amount (USD)</label>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
            )}
            {action?.kind === "pause" && (
              <div>
                <label className="text-xs font-medium">Linked dispute id</label>
                <Input
                  value={form.disputeId}
                  onChange={(e) => setForm({ ...form, disputeId: e.target.value })}
                  placeholder="dsp-…"
                  list="open-disputes"
                />
                <datalist id="open-disputes">
                  {openDisputesById.map((id) => (
                    <option key={id} value={id} />
                  ))}
                </datalist>
              </div>
            )}
            {action?.kind === "reverse" && action.state && (
              <div>
                <label className="text-xs font-medium">Entry to reverse</label>
                <select
                  className="w-full rounded-md border bg-card px-2 py-1.5 text-sm"
                  value={form.reverseEntryId}
                  onChange={(e) => setForm({ ...form, reverseEntryId: e.target.value })}
                >
                  {action.state.entries.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.action} • ${e.amount} • {e.createdAt.slice(0, 16)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium">Reason (≥ 10 characters)</label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={3}
              />
            </div>
            {error && <div className="text-xs text-destructive">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)}>
              Cancel
            </Button>
            <Button onClick={submit}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
